import { Dayts, handleError, minutesToOffset } from '@/utils';
import { ReportInput, ReportSeriesEnum, ReportTypeEnum } from '@/models/gen/graphql';
import ReportQuery, { ReportQueryAsCsv } from '@/api/queries/reports/ReportQuery';
import createGraphApiHook, { CreateGraphApiHookReturn } from '@/hooks/createGraphApiHook';

import { GraphApiResponse } from '@/api/core';
import graphApi from '../..';
import useSession from '@/state/session';

export const runReportQuery = (reportType: string, depth?: number) => graphApi(ReportQuery(reportType, depth));
export const TIMEZONE_OFFSET_MINUTES = minutesToOffset(new Dayts().utcOffset());

export type ReportQueryInput = ReportInput;
export const reportQuery =
  (reportType: string, depth?: number): ((input: ReportQueryInput) => Promise<any>) =>
  async (input: ReportQueryInput): Promise<any> => {
    input.series = input?.series || ReportSeriesEnum.SeriesDefault;
    input.csvColumns = Object.values(input?.csvColumns || {})?.length > 0 ? input?.csvColumns : null;
    // TODO: OMG, FIX THIS
    input.reason = Object.values(input?.reason || {})?.length > 0 ? input?.reason : null;
    input.buckets = Object.values(input?.buckets || {})?.length > 0 ? input?.buckets : null;
    input.complexFilters = Object.values(input?.complexFilters || {})?.length > 0 ? input?.complexFilters : null;
    // Callers rendering on behalf of another user (e.g. the PDF generator) pass the user's timezone explicitly
    input.userSessionTimezone = input?.userSessionTimezone || TIMEZONE_OFFSET_MINUTES;
    Object.entries(input).forEach(([key, value]: [string, string | string[]]) => {
      switch (key) {
        case 'startDatetime':
        case 'endDatetime':
        case 'groupBy':
        case 'status':
          value = Array.isArray(value) ? value[0] : value;
          break;
      }
      input[key] = value;
    });

    switch (reportType) {
      case 'trainingReport': {
        const res = (await runReportQuery('genericReport', depth)[1]({
          input: {
            ...input,
            roleIds: input?.roleIds?.length ? input.roleIds : [useSession.getState().state.config?.driverRoleId],
            reportType: ReportTypeEnum.TrainingReport,
          },
        } as never)) as GraphApiResponse;
        return res?.reportQuery?.genericReport || {};
      }
      case 'clientSummaryReportSnapshot': {
        const res = (await runReportQuery('genericReport', depth)[1]({
          input: {
            ...input,
            reportType: 'CLIENT_SUMMARY_REPORT_SNAPSHOT',
            snapshotIds: Array.isArray(input?.snapshotIds) ? input?.snapshotIds : [input?.snapshotIds],
          },
        } as never)) as GraphApiResponse;
        return res?.reportQuery?.genericReport || {};
      }
      case 'clientSummaryReport': {
        const res = (await runReportQuery('genericReport', depth)[1]({
          input: {
            ...input,
            reportType: 'CLIENT_SUMMARY_REPORT_DEPRECATED',
          },
        } as never)) as GraphApiResponse;
        return res?.reportQuery?.genericReport || {};
      }
      default: {
        const res = (await runReportQuery(reportType, depth)[1]({ input } as never)) as GraphApiResponse;
        return res?.reportQuery?.[reportType] || {};
      }
    }
  };

export const useReportQuery = (reportType: string, depth?: number): CreateGraphApiHookReturn<any> =>
  createGraphApiHook(reportQuery(reportType, depth))();

export const runReportQueryAsCsv = (reportType: string, depth?: number) => graphApi(ReportQueryAsCsv(reportType, depth));
export const reportQueryAsCsv =
  (reportType: string, depth?: number): ((input: ReportInput) => Promise<any>) =>
  async (input: ReportInput): Promise<any> => {
    try {
      if (reportType === 'trainingReport') {
        input = { ...input, roleIds: input?.roleIds?.length ? input.roleIds : [useSession.getState().state.config?.driverRoleId] };
      }
      // The Client Summary Report is served by the clientSummaryReportOld (MySQL) CSV query.
      const csvReportType = reportType === 'clientSummaryReport' ? 'clientSummaryReportOld' : reportType;
      const res = (await runReportQueryAsCsv(csvReportType, depth)[1]({ input } as never)) as any;
      return res?.reportQuery?.[`${csvReportType}AsCsv`]?.output || '';
    } catch (err) {
      handleError(err, { notification: { title: 'Export CSV' } });
    }
  };

// TODO: Inplement Zod validation for input.
export const reportQueryInputKeys = [
  'airlines',
  'airports',
  'buckets',
  'clients',
  'companies',
  'complexFilters',
  'csvColumns',
  'currencies',
  'details',
  'doLocations',
  'drivers',
  'endDatetime',
  'groupBy',
  'kinds',
  'puLocations',
  'rateTypes',
  'rates',
  'rating',
  'reason',
  'reportType',
  'series',
  'startDatetime',
  'status',
  'thresholds',
  'types',
  'userSessionTimezone',
  'snapshotIds',
  'fcrReportedBy',
  'charities',
];

export default reportQuery;
